import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import TurndownService from 'turndown';
import { marked } from 'marked';
import {
  Bold, Italic, Underline as U, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code, Link as LinkIcon, Image as ImgIcon,
  AlignLeft, AlignCenter, AlignRight, Undo, Redo, Code2, FileCode2, Maximize2, Minimize2,
} from 'lucide-react';
import api from '../lib/api';

const Btn = ({ active, onClick, children, title }) => (
  <button type="button" title={title} onClick={onClick}
    className={`w-8 h-8 grid place-items-center rounded-md text-sm transition-colors ${active ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
    {children}
  </button>
);
const Sep = () => <div className="w-px h-6 bg-slate-200 mx-1" />;

const RichTextEditor = ({ value, onChange, placeholder = 'Write here...', minHeight = 240 }) => {
  const [mode, setMode] = useState('rich'); // rich | html | markdown
  const [full, setFull] = useState(false);
  const [source, setSource] = useState(value || '');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const td = useRef(new TurndownService({ headingStyle: 'atx', codeBlockStyle: 'fenced' }));

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-teal-700 underline underline-offset-2' } }),
      Image.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full h-auto my-2' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
      setSource(html);
    },
  });

  // Sync external value into editor once (or when it changes drastically)
  useEffect(() => {
    if (!editor) return;
    if ((value || '') !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
      setSource(value || '');
    }
    // eslint-disable-next-line
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL', prev || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      editor.chain().focus().setImage({ src: data.url, alt: file.name }).run();
    } catch (e) {
      alert('Upload failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const addImageUrl = () => {
    const url = window.prompt('Image URL');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const applySource = () => {
    let html = source;
    if (mode === 'markdown') {
      html = marked.parse(source);
    }
    editor.commands.setContent(html || '', { emitUpdate: true });
    onChange?.(html);
    setMode('rich');
  };

  const switchMode = (target) => {
    if (target === mode) return;
    if (target === 'html') setSource(editor.getHTML());
    else if (target === 'markdown') setSource(td.current.turndown(editor.getHTML()));
    setMode(target);
  };

  return (
    <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden ${full ? 'fixed inset-4 z-50 shadow-2xl flex flex-col' : ''}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <Btn title="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo className="w-4 h-4" /></Btn>
        <Btn title="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo className="w-4 h-4" /></Btn>
        <Sep />
        <Btn title="H1" active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 className="w-4 h-4" /></Btn>
        <Btn title="H2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></Btn>
        <Btn title="H3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></Btn>
        <Sep />
        <Btn title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></Btn>
        <Btn title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></Btn>
        <Btn title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><U className="w-4 h-4" /></Btn>
        <Btn title="Strike" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="w-4 h-4" /></Btn>
        <Sep />
        <Btn title="Bulleted list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></Btn>
        <Btn title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></Btn>
        <Btn title="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="w-4 h-4" /></Btn>
        <Btn title="Code block" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}><Code className="w-4 h-4" /></Btn>
        <Sep />
        <Btn title="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}><AlignLeft className="w-4 h-4" /></Btn>
        <Btn title="Align center" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}><AlignCenter className="w-4 h-4" /></Btn>
        <Btn title="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}><AlignRight className="w-4 h-4" /></Btn>
        <Sep />
        <Btn title="Link" active={editor.isActive('link')} onClick={setLink}><LinkIcon className="w-4 h-4" /></Btn>
        <Btn title="Image URL" onClick={addImageUrl}><ImgIcon className="w-4 h-4" /></Btn>
        <button type="button" title="Upload image" onClick={() => fileRef.current?.click()} className="px-2 h-8 text-xs rounded-md text-slate-700 hover:bg-slate-100 disabled:opacity-50" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ''; }} />

        <div className="ml-auto flex items-center gap-1">
          <div className="flex bg-white rounded-md border border-slate-200 overflow-hidden text-xs">
            {['rich', 'html', 'markdown'].map((m) => (
              <button key={m} type="button" onClick={() => switchMode(m)}
                className={`px-2.5 py-1 ${mode === m ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                {m === 'rich' ? 'Rich' : m === 'html' ? <><FileCode2 className="w-3 h-3 inline" /> HTML</> : <><Code2 className="w-3 h-3 inline" /> MD</>}
              </button>
            ))}
          </div>
          <Btn title={full ? 'Compact view' : 'Full view'} onClick={() => setFull(!full)}>
            {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Btn>
        </div>
      </div>

      {mode === 'rich' ? (
        <div className={`prose prose-slate max-w-none px-4 py-3 overflow-y-auto ${full ? 'flex-1' : ''}`} style={{ minHeight: full ? undefined : minHeight }}>
          <EditorContent editor={editor} />
        </div>
      ) : (
        <div className={`px-4 py-3 ${full ? 'flex-1 flex flex-col' : ''}`}>
          <textarea
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="w-full font-mono text-xs border border-slate-200 rounded-lg p-3 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            style={{ minHeight: full ? undefined : minHeight, height: full ? '100%' : undefined }}
          />
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={applySource} className="px-3 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-xs">Apply</button>
            <button type="button" onClick={() => switchMode('rich')} className="px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 text-xs hover:bg-slate-50">Cancel</button>
            <div className="text-xs text-slate-500 self-center ml-auto">Editing as {mode.toUpperCase()}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
